/*
 * FCM Internal Payment Testing cart controller.
 * Uses only Shopify Ajax Cart endpoints. It emits no analytics events and never
 * clears existing cart contents without an explicit staff confirmation.
 */

(() => {
  'use strict';

  const initializeRoot = (root) => {
    if (root.dataset.fcPaymentTestInitialized === 'true') return;
    root.dataset.fcPaymentTestInitialized = 'true';

    const config = {
      cartUrl: root.dataset.cartUrl,
      addUrl: root.dataset.cartAddUrl,
      changeUrl: root.dataset.cartChangeUrl,
      clearUrl: root.dataset.cartClearUrl,
      updateUrl: root.dataset.cartUpdateUrl,
      checkoutUrl: root.dataset.checkoutUrl,
      locale: root.dataset.locale || 'en-MY',
      currency: root.dataset.currency || 'MYR',
      marker: root.dataset.marker || 'FCM_INTERNAL_PAYMENT_TEST'
    };

    const elements = {
      addButtons: Array.from(root.querySelectorAll('[data-add-test-product]')),
      cartItems: root.querySelector('[data-cart-items]'),
      cartLoading: root.querySelector('[data-cart-loading]'),
      cartTotal: root.querySelector('[data-cart-total]'),
      cartTotalRow: root.querySelector('[data-cart-total-row]'),
      cartWarning: root.querySelector('[data-cart-warning]'),
      checkout: root.querySelector('[data-checkout]'),
      reset: root.querySelector('[data-reset-cart]'),
      status: root.querySelector('[data-status]')
    };

    let requestInProgress = false;
    let currentCart = null;

    const setStatus = (message = '', type = '') => {
      elements.status.textContent = message;
      if (type) {
        elements.status.dataset.type = type;
      } else {
        delete elements.status.dataset.type;
      }
    };

    const setBusy = (busy, message = '') => {
      requestInProgress = busy;
      root.setAttribute('aria-busy', String(busy));
      elements.addButtons.forEach((button) => {
        const select = button.closest('[data-product-card]')?.querySelector('[data-variant-select]');
        button.disabled = busy || hasExternalItems(currentCart) || select?.dataset.available !== 'true';
      });
      elements.checkout.disabled = busy || !canCheckout(currentCart);
      elements.reset.disabled = busy || !canReset(currentCart);
      if (message) setStatus(message);
    };

    const request = async (url, options = {}) => {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          ...(options.body ? { 'Content-Type': 'application/json' } : {}),
          ...options.headers
        },
        ...options
      });

      let payload = null;
      try {
        payload = await response.json();
      } catch (error) {
        payload = null;
      }

      if (!response.ok) {
        const message = payload?.description || payload?.message || `Shopify returned ${response.status}.`;
        throw new Error(message);
      }

      return payload;
    };

    const getCart = () => request(config.cartUrl);

    const isInternalLine = (item) => {
      const properties = item?.properties || {};
      return properties._FCM_INTERNAL_PAYMENT_TEST === config.marker;
    };

    const hasExternalItems = (cart) => Boolean(cart?.items?.some((item) => !isInternalLine(item)));
    const hasInternalItems = (cart) => Boolean(cart?.items?.some(isInternalLine));
    const canCheckout = (cart) => Boolean(cart?.item_count > 0 && !hasExternalItems(cart) && cart.items.every(isInternalLine));
    const canReset = (cart) => Boolean(cart?.item_count > 0 && hasInternalItems(cart) && !hasExternalItems(cart));

    const formatMoney = (cents, currency = config.currency) => {
      try {
        return new Intl.NumberFormat(config.locale, {
          style: 'currency',
          currency: currency || 'MYR',
          currencyDisplay: 'code'
        }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `${currency || 'MYR'} ${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    };

    const createElement = (tag, className, text) => {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (typeof text === 'string') element.textContent = text;
      return element;
    };

    const renderCartLine = (item) => {
      const internal = isInternalLine(item);
      const line = createElement('div', 'fc-payment-test__cart-line');
      const media = createElement('div', 'fc-payment-test__cart-line-image');

      if (item.featured_image?.url) {
        const image = document.createElement('img');
        image.src = item.featured_image.url;
        image.alt = item.featured_image.alt || item.product_title || '';
        image.width = 124;
        image.height = 124;
        image.loading = 'lazy';
        media.appendChild(image);
      }

      const details = createElement('div', 'fc-payment-test__cart-line-details');
      details.appendChild(createElement('span', 'fc-payment-test__cart-line-title', item.product_title));

      if (item.variant_title && item.variant_title !== 'Default Title') {
        details.appendChild(createElement('span', 'fc-payment-test__cart-line-meta', item.variant_title));
      }

      details.appendChild(createElement(
        'span',
        'fc-payment-test__cart-line-meta',
        internal ? 'Marked internal payment test' : 'Existing customer cart item — protected'
      ));
      details.appendChild(createElement(
        'span',
        'fc-payment-test__cart-line-price',
        formatMoney(item.final_line_price ?? item.line_price, currentCart?.currency)
      ));

      line.appendChild(media);
      line.appendChild(details);

      if (internal) {
        const remove = createElement('button', 'fc-payment-test__remove', 'Remove');
        remove.type = 'button';
        remove.dataset.removeLine = item.key;
        remove.addEventListener('click', () => removeInternalLine(item.key));
        line.appendChild(remove);
      } else {
        line.appendChild(createElement('span', 'fc-payment-test__cart-line-meta', 'Protected'));
      }

      return line;
    };

    const renderCart = (cart) => {
      currentCart = cart;
      config.currency = cart.currency || config.currency;
      elements.cartLoading.hidden = true;
      elements.cartItems.hidden = false;
      elements.cartItems.replaceChildren();

      if (!cart.item_count) {
        elements.cartItems.appendChild(createElement('div', 'fc-payment-test__cart-empty', 'The test cart is empty.'));
        elements.cartTotalRow.hidden = true;
      } else {
        cart.items.forEach((item) => elements.cartItems.appendChild(renderCartLine(item)));
        elements.cartTotal.textContent = formatMoney(cart.total_price, cart.currency);
        elements.cartTotalRow.hidden = false;
      }

      const external = hasExternalItems(cart);
      if (external) {
        elements.cartWarning.hidden = false;
        elements.cartWarning.textContent = 'Existing customer cart items were detected. Adding and checkout are blocked to prevent mixing them with payment-test products. Remove normal items through the normal cart, or use a separate clean browser session.';
      } else {
        elements.cartWarning.hidden = true;
        elements.cartWarning.textContent = '';
      }

      elements.addButtons.forEach((button) => {
        const select = button.closest('[data-product-card]')?.querySelector('[data-variant-select]');
        button.disabled = requestInProgress || external || select?.dataset.available !== 'true';
      });
      elements.checkout.disabled = requestInProgress || !canCheckout(cart);
      elements.reset.disabled = requestInProgress || !canReset(cart);
    };

    const refreshCart = async () => {
      const cart = await getCart();
      renderCart(cart);
      return cart;
    };

    const clearCartMarkers = () => request(config.updateUrl, {
      method: 'POST',
      body: JSON.stringify({
        attributes: {
          FCM_INTERNAL_PAYMENT_TEST: '',
          FCM_INTERNAL_PAYMENT_TEST_SOURCE: ''
        }
      })
    });

    const clearInternalCart = async () => {
      await request(config.clearUrl, {
        method: 'POST',
        body: JSON.stringify({})
      });
      await clearCartMarkers();
    };

    const removeInternalLine = async (key) => {
      if (requestInProgress) return;
      setBusy(true, 'Removing the test item…');

      try {
        const freshCart = await getCart();
        const target = freshCart.items.find((item) => item.key === key);
        if (!target || !isInternalLine(target)) {
          throw new Error('That line is no longer an internal test item. The cart was not changed.');
        }

        await request(config.changeUrl, {
          method: 'POST',
          body: JSON.stringify({ id: key, quantity: 0 })
        });

        const updatedCart = await getCart();
        if (!hasInternalItems(updatedCart)) {
          await clearCartMarkers();
        }
        renderCart(await getCart());
        setStatus('Test item removed.', 'success');
      } catch (error) {
        await refreshCart().catch(() => {});
        setStatus(error.message || 'The test item could not be removed.', 'error');
      } finally {
        setBusy(false);
      }
    };

    const addTestProduct = async (button) => {
      if (requestInProgress) return;

      const card = button.closest('[data-product-card]');
      const selector = card?.querySelector('[data-variant-select]');
      const selectedOption = selector?.tagName === 'SELECT' ? selector.selectedOptions[0] : selector;
      const variantId = Number(selector?.value);

      if (!variantId || selectedOption?.dataset.available !== 'true') {
        setStatus('Select an available variant before adding it.', 'error');
        return;
      }

      setBusy(true, 'Checking the current cart…');

      try {
        const cart = await getCart();
        renderCart(cart);

        if (hasExternalItems(cart)) {
          throw new Error('Existing customer items are protected. Use a separate clean browser session for this payment test.');
        }

        if (cart.item_count > 0) {
          const confirmed = window.confirm('Replace the current internal test cart with one unit of this selected variant?');
          if (!confirmed) {
            setStatus('The existing test cart was left unchanged.');
            return;
          }
          await clearInternalCart();
        }

        await request(config.addUrl, {
          method: 'POST',
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: {
                  _FCM_INTERNAL_PAYMENT_TEST: config.marker,
                  _FCM_INTERNAL_PAYMENT_TEST_SOURCE: 'internal-payment-test-page'
                }
              }
            ]
          })
        });

        try {
          await request(config.updateUrl, {
            method: 'POST',
            body: JSON.stringify({
              attributes: {
                FCM_INTERNAL_PAYMENT_TEST: config.marker,
                FCM_INTERNAL_PAYMENT_TEST_SOURCE: 'internal-payment-test-page'
              }
            })
          });
        } catch (markerError) {
          renderCart(await getCart());
          throw new Error('The product was added, but the cart-level test marker could not be saved. Reset the test cart and retry before checkout.');
        }

        renderCart(await getCart());
        setStatus('Test product added. Review the cart before checkout.', 'success');
      } catch (error) {
        await refreshCart().catch(() => {});
        setStatus(error.message || 'The test product could not be added.', 'error');
      } finally {
        setBusy(false);
      }
    };

    const resetTestCart = async () => {
      if (requestInProgress) return;
      setBusy(true, 'Checking the current cart…');

      try {
        const cart = await getCart();
        renderCart(cart);

        if (!canReset(cart)) {
          throw new Error('The cart was not reset because it is empty or contains protected customer items.');
        }

        const confirmed = window.confirm('Clear all internal payment-test items from this cart?');
        if (!confirmed) {
          setStatus('The test cart was left unchanged.');
          return;
        }

        await clearInternalCart();
        renderCart(await getCart());
        setStatus('The internal test cart was reset.', 'success');
      } catch (error) {
        await refreshCart().catch(() => {});
        setStatus(error.message || 'The test cart could not be reset.', 'error');
      } finally {
        setBusy(false);
      }
    };

    const proceedToCheckout = async () => {
      if (requestInProgress) return;
      setBusy(true, 'Rechecking the cart before checkout…');

      try {
        const cart = await getCart();
        renderCart(cart);

        if (!canCheckout(cart)) {
          throw new Error('Checkout is blocked because the cart is empty or contains an unmarked customer item.');
        }

        window.location.assign(config.checkoutUrl);
      } catch (error) {
        setStatus(error.message || 'Checkout could not be opened.', 'error');
        setBusy(false);
      }
    };

    root.querySelectorAll('[data-variant-select]').forEach((selector) => {
      const updateVariant = () => {
        const card = selector.closest('[data-product-card]');
        const option = selector.tagName === 'SELECT' ? selector.selectedOptions[0] : selector;
        const available = option?.dataset.available === 'true';
        const price = card?.querySelector('[data-product-price]');
        const availability = card?.querySelector('[data-availability]');
        const button = card?.querySelector('[data-add-test-product]');
        const image = card?.querySelector('[data-product-image]');

        selector.dataset.available = String(available);
        if (price && option?.dataset.priceFormatted) price.textContent = option.dataset.priceFormatted;
        if (availability) {
          availability.textContent = available ? 'Available for testing' : 'Currently unavailable';
          availability.dataset.unavailable = String(!available);
        }
        if (image && option?.dataset.imageUrl) image.src = option.dataset.imageUrl;
        if (button) button.disabled = requestInProgress || hasExternalItems(currentCart) || !available;
      };

      selector.addEventListener('change', updateVariant);
      updateVariant();
    });

    elements.addButtons.forEach((button) => button.addEventListener('click', () => addTestProduct(button)));
    elements.reset.addEventListener('click', resetTestCart);
    elements.checkout.addEventListener('click', proceedToCheckout);

    refreshCart().catch((error) => {
      elements.cartLoading.hidden = true;
      elements.cartWarning.hidden = false;
      elements.cartWarning.textContent = 'The Shopify cart could not be loaded. Adding and checkout remain disabled.';
      setStatus(error.message || 'Unable to load the cart.', 'error');
    });
  };

  document.querySelectorAll('[data-fc-payment-test]').forEach(initializeRoot);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-fc-payment-test]').forEach(initializeRoot);
  });
})();
